import { formatPhoneNumber } from 'util/formatPhoneNumber';
import { Box } from '@mui/material';
import { Button, FormErrors, TextInput } from 'components/ui';
import { signUpDetailsFormSchema, SignUpDetailsFormData } from 'forms';
import { mutateProfile, useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useAuthApi } from 'services/api';

export interface SignUpStayConnectedFormProps {
  onSubmit: () => void;
}

export const SignUpStayConnectedForm = ({
  onSubmit: onSubmitProp,
}: SignUpStayConnectedFormProps): ReactElement => {
  const { profile } = useProfile();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, touchedFields: touched, isSubmitting },
  } = useAppForm(signUpDetailsFormSchema, {
    mode: 'all',
  });

  const authApi = useAuthApi();

  const onSubmit = async (data: SignUpDetailsFormData) => {
    await authApi.confirmPersonalDetails(data);
    void mutateProfile();
    onSubmitProp?.();
  };

  const ref = useRef<HTMLInputElement>(null);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mt={10}>
        <TextInput
          label="Zip Code"
          placeholder="00000"
          hasError={!!touched.zipCode && !!errors.zipCode}
          defaultValue={profile?.zipCode ?? ''}
          {...register('zipCode')}
        />
        <FormErrors sx={{ whiteSpace: 'unset', width: '100%' }}>
          {errors.zipCode?.message}
        </FormErrors>
      </Box>

      <Box mt={10}>
        <Controller
          name="mobileNumber"
          control={control}
          defaultValue={profile?.mobileNumber ?? ''}
          render={({ field: { value, onChange, ref: _ref, ...field } }) => {
            return (
              <TextInput
                label="Phone Number"
                placeholder="(000)000-0000"
                ref={ref}
                value={
                  ref.current?.selectionStart === undefined ||
                  (ref.current?.selectionStart ?? 0) >= value.length
                    ? formatPhoneNumber(value?.replace(/\D/g, '').slice(0, 10))
                    : `${value}`
                }
                onChange={e => {
                  const value = e.target.value;
                  const transformedValue =
                    (e.target?.selectionStart ?? -1) >= value.length
                      ? value.replace(/\D/g, '').slice(0, 10)
                      : value;
                  onChange(transformedValue);
                }}
                hasError={touched.mobileNumber && !!errors.mobileNumber}
                {...field}
              />
            );
          }}
        />
        <FormErrors sx={{ whiteSpace: 'unset', width: '100%' }}>
          {errors.mobileNumber?.message}
        </FormErrors>
      </Box>
      <Box mt={32}>
        <Button
          type="submit"
          minWidth="100%"
          variant="vibrant"
          minHeight={47}
          disabled={isSubmitting}
        >
          Next
        </Button>
      </Box>
    </form>
  );
};
